export type BotMode = "paper" | "live";
export type BotStatus = "stopped" | "running" | "paused" | "attention";
export type SignalState = "pass" | "wait" | "fail";
export type TradeStatus = "open" | "closed" | "cancelled" | "rejected";

export interface SetupStatus {
  setupRequired: boolean;
}

export interface SessionInfo {
  authenticated: boolean;
  csrfToken?: string;
}

export interface StrategySettings {
  preset: "conservative" | "standard" | "aggressive" | "custom";
  amountUsd: number;
  allocationPercent: number;
  minimumNetEdgePercent: number;
  grossTargetPercent: number;
  stopDistancePercent: number;
  maxHoldMinutes: number;
  lossStreakLimit: number | null;
  reinvestProfits: boolean;
  slippageAllowancePercent: number;
}

export interface SignalCheck {
  id: string;
  label: string;
  state: SignalState;
  detail: string;
}

export interface MarketSnapshot {
  pair: "ETH/USD";
  price: number | null;
  bid: number | null;
  ask: number | null;
  change24hPercent: number | null;
  range24hPercent: number | null;
  sparkline: Array<{ time: number; price: number }>;
  updatedAt: string | null;
  stale: boolean;
  systemStatus: string;
}

export interface FeeSnapshot {
  makerPercent: number | null;
  takerPercent: number | null;
  source: "authenticated" | "unavailable";
  updatedAt: string | null;
}

export interface PortfolioSnapshot {
  totalUsd: number | null;
  usdAvailable: number | null;
  ethAvailable: number | null;
  todayNetPnlUsd: number;
  todayFeesUsd: number;
}

export interface PositionSnapshot {
  id: string;
  side: "buy";
  status: "pending-entry" | "open" | "pending-exit";
  mode: BotMode;
  quantityEth: number;
  entryPrice: number | null;
  currentPrice: number | null;
  targetPrice: number | null;
  stopPrice: number | null;
  grossPnlUsd: number | null;
  estimatedFeesUsd: number | null;
  estimatedNetPnlUsd: number | null;
  openedAt: string;
  reason: string;
}

export interface TradeLedgerItem {
  id: string;
  mode: BotMode;
  status: TradeStatus;
  closedAt: string | null;
  quantityEth: number;
  entryPrice: number;
  exitPrice: number | null;
  grossPnlUsd: number | null;
  feesUsd: number;
  netPnlUsd: number | null;
  reason: string;
}

export interface ActivityEvent {
  id: string;
  severity: "info" | "success" | "warning" | "error";
  title: string;
  detail: string;
  createdAt: string;
}

export interface ConnectionSnapshot {
  connected: boolean;
  keyName: string | null;
  keyHint: string | null;
  permissions: string[];
  missingPermissions: string[];
  forbiddenPermissions: string[];
  ipAllowlistDetected: boolean;
  validUntil: string | null;
  lastCheckedAt: string | null;
  privateKeyStored: boolean;
  message: string;
}

export interface DashboardSnapshot {
  bot: {
    mode: BotMode;
    status: BotStatus;
    pair: "ETH/USD";
    strategyName: "ETH Momentum-Pullback";
    message: string;
    updatedAt: string;
    liveAcknowledged: boolean;
  };
  connection: ConnectionSnapshot;
  market: MarketSnapshot;
  fees: FeeSnapshot;
  portfolio: PortfolioSnapshot;
  position: PositionSnapshot | null;
  signals: SignalCheck[];
  strategy: StrategySettings;
  recentTrades: TradeLedgerItem[];
  recentEvents: ActivityEvent[];
}

export interface ConnectKrakenRequest {
  apiKey: string;
  apiSecret: string;
}

export interface ConnectKrakenResponse {
  connection: ConnectionSnapshot;
  fees: FeeSnapshot;
}

export interface ApiErrorShape {
  error: string;
  code: string;
  details?: string[];
}

export const DEFAULT_STRATEGY: StrategySettings = {
  preset: "standard",
  amountUsd: 200,
  allocationPercent: 25,
  minimumNetEdgePercent: 1.25,
  grossTargetPercent: 2.8,
  stopDistancePercent: 0.6,
  maxHoldMinutes: 90,
  lossStreakLimit: 2,
  reinvestProfits: false,
  slippageAllowancePercent: 0.1,
};

